import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Interface for knowledge data (always internal, no company)
export interface Knowledge {
  id: string;
  knowledgeName: string;
  knowledgeDescription?: string;
  [key: string]: any; // Allow additional properties
}

export const useKnowledge = (autoFetch: boolean = true) => {
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKnowledge = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const knowledgeCollection = collection(db, 'knowledge');
      const knowledgeSnapshot = await getDocs(knowledgeCollection);

      const knowledgeData: Knowledge[] = [];
      knowledgeSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        knowledgeData.push({
          id: docSnap.id,
          knowledgeName: data.knowledgeName || '',
          knowledgeDescription: data.knowledgeDescription || '',
          ...data
        } as Knowledge);
      });

      // Sort by name
      knowledgeData.sort((a, b) => a.knowledgeName.localeCompare(b.knowledgeName));

      setKnowledge(knowledgeData);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ useKnowledge: Error fetching knowledge:', err);
      }
      setError('Failed to load knowledge');
      setKnowledge([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchKnowledge();
    }
  }, [fetchKnowledge, autoFetch]);

  const getKnowledgeById = useCallback(async (knowledgeId: string): Promise<Knowledge | null> => {
    try {
      const knowledgeRef = doc(db, 'knowledge', knowledgeId);
      const knowledgeDoc = await getDoc(knowledgeRef);

      if (!knowledgeDoc.exists()) {
        return null;
      }

      const data = knowledgeDoc.data();
      return {
        id: knowledgeDoc.id,
        knowledgeName: data.knowledgeName || '',
        knowledgeDescription: data.knowledgeDescription || '',
        ...data
      } as Knowledge;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ useKnowledge: Error fetching knowledge by ID:', error);
      }
      return null;
    }
  }, []);

  const updateKnowledge = useCallback(async (knowledgeId: string, data: { knowledgeName?: string; knowledgeDescription?: string }): Promise<void> => {
    const knowledgeRef = doc(db, 'knowledge', knowledgeId);
    await updateDoc(knowledgeRef, data);
  }, []);

  return {
    knowledge,
    loading,
    error,
    refetch: fetchKnowledge,
    getKnowledgeById,
    updateKnowledge
  };
};
